import {
  BillOfMaterialRecord,
  BOMNextStatus,
  BOMStatusForm,
} from '../../../../../types/BillOfMaterial';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../store';
import { Button, Card, Form, Input, Space, Timeline } from 'antd';
import { useUpdateStatus } from '../../../../../hooks';
import { BOM_API } from '../../../../../hooks/useBillOfMaterialHooks';
import { BOMStatusEnum } from '../../../../../lib/enums';
import { isModuleApproved } from '../../../../../lib/utils';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import { TimelineItemProps } from 'antd/es/timeline/TimelineItem';
import TimelineView from '../../../../../components/TimelineView';
import { StatusHistory } from '../../../../../types';

interface BillOfMaterialInfoStatusProps {
  data?: BillOfMaterialRecord;
  statusHistory: StatusHistory[];
}
const BillOfMaterialInfoStatus: React.FC<BillOfMaterialInfoStatusProps> = ({
  data,
  statusHistory,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [form] = Form.useForm();

  const {
    mutate,
    isPending,
    isError: isUpdateError,
    error: updateError,
    isSuccess,
  } = useUpdateStatus(`${BOM_API}/${data?.id}/status`);

  const [cancelClicked, setCancelClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  const onFinish = (values: any) => {
    const form: BOMStatusForm = {
      status: BOMNextStatus(data?.status),
      remarks: values.remarks,
    };
    mutate({
      id: data?.id,
      form: form,
    });
  };

  useEffect(() => {
    if (isSuccess) setIsInfoModalOpen(true);
    if (isUpdateError) {
      setIsErrorModalOpen(true);
      setErrorMess(updateError?.response?.data.message);
    }
  }, [isSuccess, isUpdateError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
    // router.push('/purchase-order');
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/production/bill-of-material');
  };
  const handleCancelClick = () => {
    const textValue = form.getFieldValue('remarks');
    if (!textValue?.trim()) {
      setCancelClicked(true);
      setErrorMessage('Text is required');
      setTimeout(() => {
        setErrorMessage('');
      }, 2000);
    } else {
      setCancelClicked(false);
      const form: BOMStatusForm = {
        status: BOMStatusEnum.CANCEL,
        remarks: textValue,
      };
      mutate({
        id: data?.id,
        form: form,
      });
    }
  };

  const renderCancelButton = () => {
    const isAdmin = user?.role === 'ROLE_ADMIN';
    const canCancel =
      data?.status === BOMStatusEnum.CONFIRM ||
      data?.status === BOMStatusEnum.APPROVED ||
      data?.status === BOMStatusEnum.READY;

    if ((isAdmin && canCancel) || canCancel) {
      return (
        <Button danger onClick={handleCancelClick} loading={isPending}>
          Cancel
        </Button>
      );
    }
    return null;
  };

  let apiTimelineItems: TimelineItemProps[] = [];

  if (statusHistory && statusHistory.length > 0) {
    apiTimelineItems = statusHistory
      .map((item) => ({
        key: item.id,
        children: <TimelineView item={item} />,
      }))
      .sort((a, b) => {
        return parseInt(b.key) - parseInt(a.key);
      });
  }

  const timelineItems = [
    {
      key: 'status',
      children: (
        <div className={'space-y-2'}>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item
              name="remarks"
              rules={[
                { required: cancelClicked, message: 'Please input your text!' },
              ]}
              validateStatus={cancelClicked ? 'error' : undefined}
              className={cancelClicked ? 'ant-form-item-has-error' : ''}
              help={cancelClicked ? errorMessage : ''}
            >
              <Input.TextArea rows={4} placeholder="Input your text here" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isPending}>
                  {BOMNextStatus(data?.status)}
                </Button>
                {renderCancelButton()}
              </Space>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    ...apiTimelineItems,
    {
      key: 'Created',
      children: (
        <div className={'space-y-2 border rounded p-2'}>
          <div>
            <span className={'font-semibold'}>{data?.code}</span> requested by{' '}
            <span className={'font-semibold'}>{data?.createdBy}</span> requested
            date <span className={'font-semibold'}>{data?.timeCreated}</span>.
          </div>
        </div>
      ),
    },
  ];

  const filteredTimelineItems = timelineItems.filter((item) => {
    // if (item.key === 'status') {
    //   const recStatus = data?.status;
    //   const userRole = user?.role;
    //
    //   if (userRole === 'ROLE_ADMIN') {
    //     return !(
    //       recStatus === BOMStatusEnum.CANCEL || recStatus === BOMStatusEnum.DONE
    //     );
    //   }
    //
    //   if (
    //     recStatus === BOMStatusEnum.CANCEL ||
    //     recStatus === BOMStatusEnum.DONE
    //   ) {
    //     return false;
    //   }
    //
    //   return isModuleApproved(user?.moduleAccess, 'BOM');
    // }
    //
    // return true;
    if (item.key !== 'status') {
      return true; // Pass through items that are not related to status
    }
    const recStatus = data?.status;
    const userRole = user?.role;
    const userModuleAccess = user?.moduleAccess;

    const isApproverOrAdmin = () => {
      return (
        userRole === 'ROLE_ADMIN' ||
        isModuleApproved(userModuleAccess, 'BillOfMaterial')
      );
    };
    switch (recStatus) {
      case BOMStatusEnum.CANCEL:
      case BOMStatusEnum.AVAILABLE:
      case BOMStatusEnum.DONE:
      case BOMStatusEnum.DRAFT:
        return false; // Filter out CANCEL and DONE statuses
      case BOMStatusEnum.CONFIRM:
      case BOMStatusEnum.READY:
      case BOMStatusEnum.APPROVED:
        return isApproverOrAdmin();
      default:
        return false; // Filter out any other statuses
    }
  });
  return (
    <>
      <Card style={{ margin: '1rem' }} title={'Status Timeline'}>
        <Timeline reverse={false} items={filteredTimelineItems} />
      </Card>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={`Bill of Material successfully set to ${data?.status}`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default BillOfMaterialInfoStatus;
